import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { authService, RegisterRequest } from '../services/authService'
import { getApiErrorMessage } from '../utils/apiErrors'
import {
  focusFirstField,
  validateEmail,
  validateName,
  validatePassword,
  validatePasswordConfirmation,
  validateTermsAccepted,
} from '../utils/formValidation'
import { loadLoginPreferences } from '../utils/loginPreferences'
import { performLogin, unwrapPerformLoginResult } from '../utils/performLogin'
import FormFieldError from './FormFieldError'
import GoogleSignInButton from './GoogleSignInButton'
import CaptchaWidget from './CaptchaWidget'
import { redirectForRole } from '../utils/completeGoogleSignIn'
import RememberMeField from './RememberMeField'
import { useProductMode } from '../contexts/ProductModeContext'
import { useAuthCaptcha } from '../hooks/useAuthCaptcha'
import {
  captureReferralCodeFromUrl,
  clearStoredReferralCode,
  getStoredReferralCode,
} from '../services/referralService'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

type FieldErrors = Record<string, string>

const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const { humanTutorLayerEnabled } = useProductMode()
  const captcha = useAuthCaptcha()
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login')
  const [showSuccess, setShowSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showRegPassword, setShowRegPassword] = useState(false)
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false)
  const [loginErrors, setLoginErrors] = useState<FieldErrors>({})
  const [registerErrors, setRegisterErrors] = useState<FieldErrors>({})
  const [formNotice, setFormNotice] = useState<string | null>(null)

  // Form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
    rememberMe: true
  })

  const [registerForm, setRegisterForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  })

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && event.target instanceof Element && event.target.classList.contains('modal-bg')) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => document.removeEventListener('click', handleClickOutside)
  }, [isOpen, onClose])

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const prefs = loadLoginPreferences('student')
    if (prefs) {
      setLoginForm((prev) => ({
        ...prev,
        email: prefs.email,
        rememberMe: prefs.rememberMe,
      }))
    }
  }, [isOpen])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: FieldErrors = {}
    const emailError = validateEmail(loginForm.email)
    const passwordError = validatePassword(loginForm.password)
    if (emailError) errors.email = emailError
    if (passwordError) errors.password = passwordError

    if (Object.keys(errors).length > 0) {
      setLoginErrors(errors)
      setFormNotice('Check the highlighted fields and try again.')
      focusFirstField(
        { email: 'login-email', password: 'login-password' },
        errors,
      )
      return
    }

    const captchaError = captcha.missingTokenMessage()
    if (captchaError) {
      setFormNotice(captchaError)
      return
    }

    setLoginErrors({})
    setFormNotice(null)

    try {
      const userData = unwrapPerformLoginResult(await performLogin({
        portal: 'student',
        email: loginForm.email,
        password: loginForm.password,
        rememberMe: loginForm.rememberMe,
        captchaToken: captcha.captchaToken,
      }))

      onClose()

      if (userData.role === 'admin') {
        window.location.href = '/admin/dashboard'
      } else if (userData.role === 'tutor') {
        window.location.href = '/tutor/profile'
      } else if (userData.role === 'school') {
        window.location.href = '/school/dashboard'
      } else {
        window.location.href = '/messages'
      }
    } catch (err: unknown) {
      console.error('Login failed:', err)
      captcha.handleAuthError(err)
      void captcha.resetToken()
      setFormNotice(getApiErrorMessage(err, 'Login failed. Please try again.'))
    }
  }

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: FieldErrors = {}
    const firstNameError = validateName(registerForm.firstname, 'First name')
    const lastNameError = validateName(registerForm.lastname, 'Last name')
    const emailError = validateEmail(registerForm.email)
    const passwordError = validatePassword(registerForm.password, { forRegistration: true })
    const confirmError = validatePasswordConfirmation(
      registerForm.password,
      registerForm.confirmPassword,
    )
    const termsError = validateTermsAccepted(registerForm.agreeTerms)

    if (firstNameError) errors.firstname = firstNameError
    if (lastNameError) errors.lastname = lastNameError
    if (emailError) errors.email = emailError
    if (passwordError) errors.password = passwordError
    if (confirmError) errors.confirmPassword = confirmError
    if (termsError) errors.terms = termsError

    if (Object.keys(errors).length > 0) {
      setRegisterErrors(errors)
      setFormNotice('Complete the highlighted fields to create your account.')
      focusFirstField(
        {
          firstname: 'reg-firstname',
          lastname: 'reg-lastname',
          email: 'reg-email',
          password: 'reg-password',
          confirmPassword: 'reg-confirm-password',
          terms: 'agree-terms',
        },
        errors,
      )
      return
    }

    const captchaError = captcha.missingTokenMessage()
    if (captchaError) {
      setFormNotice(captchaError)
      return
    }

    setRegisterErrors({})
    setFormNotice(null)

    try {
      const registerData: RegisterRequest = {
        email: registerForm.email,
        password: registerForm.password,
        first_name: registerForm.firstname,
        last_name: registerForm.lastname,
        role: 'student',
        school_name: undefined,
      }
      if (captcha.captchaToken) {
        registerData.captcha_token = captcha.captchaToken
      }
      captureReferralCodeFromUrl()
      const ref = getStoredReferralCode()
      if (ref) {
        registerData.referral_code = ref
      }

      const registered = await authService.register(registerData)
      // Keep code if attribution failed so student can apply it from Account Settings.
      if (ref && registered?.referral_attributed) {
        clearStoredReferralCode()
      }
      setShowSuccess(true)
      setTimeout(() => {
        setShowSuccess(false)
        setActiveTab('login')
        setRegisterForm({
          firstname: '',
          lastname: '',
          email: '',
          password: '',
          confirmPassword: '',
          agreeTerms: false,
        })
      }, 2000)
    } catch (err: unknown) {
      console.error('Registration failed:', err)
      captcha.handleAuthError(err)
      void captcha.resetToken()
      setFormNotice(getApiErrorMessage(err, 'Registration failed. Please try again.'))
    }
  }

  const handleForgotPassword = () => {
    // Self-serve reset is not shipped yet — do not claim an email was sent.
    alert('Password reset is not available yet. Please contact support for help signing in.')
  }

  const togglePassword = () => setShowPassword(!showPassword)
  const toggleRegPassword = () => setShowRegPassword(!showRegPassword)
  const toggleRegConfirmPassword = () => setShowRegConfirmPassword(!showRegConfirmPassword)

  if (!isOpen) return null

  return (
    <div className="modal-bg" id="login-modal">
      <div className="modal-content">
        {/* Left side branding */}
        <div className="modal-brand-side">
          <div className="brand-logo">ZIMARK</div>
          <div className="brand-tagline">
            AI-Powered Learning Platform for Academic Excellence
          </div>
          <ul className="brand-features">
            <li>
              <i className="fas fa-robot"></i>
              AI-Powered Tutoring
            </li>
            <li>
              <i className="fas fa-users"></i>
              Expert Instructors
            </li>
            <li>
              <i className="fas fa-clock"></i>
              24/7 Learning Support
            </li>
            <li>
              <i className="fas fa-certificate"></i>
              Certified Courses
            </li>
            <li>
              <i className="fas fa-chart-line"></i>
              Progress Tracking
            </li>
          </ul>
        </div>

        {/* Right side form */}
        <div className="modal-form-side">
          <div className="modal-header">
            <div className="modal-close" onClick={onClose}>&times;</div>
            <div className="modal-logo">ZIMARK</div>
            <h2 className="modal-title">Welcome Back</h2>
            <p className="modal-subtitle">Sign in to continue your learning journey</p>
          </div>
          
          <div className="tabs">
            <div 
              className={`tab ${activeTab === 'login' ? 'active' : ''}`} 
              onClick={() => {
                setActiveTab('login')
                setFormNotice(null)
                setLoginErrors({})
                setRegisterErrors({})
              }}
            >
              Login
            </div>
            <div 
              className={`tab ${activeTab === 'register' ? 'active' : ''}`} 
              onClick={() => {
                setActiveTab('register')
                setFormNotice(null)
                setLoginErrors({})
                setRegisterErrors({})
              }}
            >
              Register
            </div>
          </div>

          <div className="modal-form-container">

        {formNotice && (
          <div className="form-notice" role="alert">
            <i className="fas fa-circle-exclamation" aria-hidden />
            <span>{formNotice}</span>
          </div>
        )}
        
        {/* Login Form */}
        <form
          id="login-form"
          noValidate
          style={{ display: activeTab === 'login' ? 'block' : 'none' }}
          onSubmit={handleLogin}
        >
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <div className="input-wrapper">
              <input 
                type="email" 
                id="login-email" 
                placeholder="Enter your email" 
                className={loginErrors.email ? 'form-input-error' : undefined}
                aria-invalid={Boolean(loginErrors.email)}
                aria-describedby={loginErrors.email ? 'login-email-error' : undefined}
                value={loginForm.email}
                onChange={(e) => {
                  setLoginForm({ ...loginForm, email: e.target.value })
                  if (loginErrors.email) setLoginErrors((prev) => ({ ...prev, email: '' }))
                }}
              />
            </div>
            <FormFieldError id="login-email-error" message={loginErrors.email} />
          </div>
          
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <div className="input-wrapper">
              <input 
                type={showPassword ? 'text' : 'password'} 
                id="login-password" 
                placeholder="Enter your password" 
                className={loginErrors.password ? 'form-input-error' : undefined}
                aria-invalid={Boolean(loginErrors.password)}
                aria-describedby={loginErrors.password ? 'login-password-error' : undefined}
                value={loginForm.password}
                onChange={(e) => {
                  setLoginForm({ ...loginForm, password: e.target.value })
                  if (loginErrors.password) setLoginErrors((prev) => ({ ...prev, password: '' }))
                }}
              />
              <i 
                className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle`} 
                id="login-password-toggle" 
                onClick={togglePassword}
              ></i>
            </div>
            <FormFieldError id="login-password-error" message={loginErrors.password} />
          </div>
          
          <div className="form-options">
            <RememberMeField
              checked={loginForm.rememberMe}
              onChange={(rememberMe) => setLoginForm({ ...loginForm, rememberMe })}
            />
            <a href="#" className="form-link" id="forgot-link" onClick={handleForgotPassword}>
              Forgot password?
            </a>
          </div>

          {captcha.showWidget && (
            <div className="form-group">
              <CaptchaWidget
                siteKey={captcha.siteKey}
                onTokenChange={captcha.onTokenChange}
                onReadyChange={captcha.onReadyChange}
              />
            </div>
          )}
          
          <button type="submit" className="form-btn">
            <i className="fas fa-sign-in-alt"></i>
            Sign In
          </button>
        </form>
        
        {/* Register Form */}
        <form
          id="register-form"
          noValidate
          style={{ display: activeTab === 'register' ? 'block' : 'none' }}
          onSubmit={handleRegisterSubmit}
        >
          <div className="form-group">
            <label htmlFor="reg-firstname">First Name</label>
            <div className="input-wrapper">
              <input 
                type="text" 
                id="reg-firstname" 
                placeholder="Enter your first name" 
                className={registerErrors.firstname ? 'form-input-error' : undefined}
                aria-invalid={Boolean(registerErrors.firstname)}
                value={registerForm.firstname}
                onChange={(e) => {
                  setRegisterForm({ ...registerForm, firstname: e.target.value })
                  if (registerErrors.firstname) setRegisterErrors((prev) => ({ ...prev, firstname: '' }))
                }}
              />
            </div>
            <FormFieldError message={registerErrors.firstname} />
          </div>
          
          <div className="form-group">
            <label htmlFor="reg-lastname">Last Name</label>
            <div className="input-wrapper">
              <input 
                type="text" 
                id="reg-lastname" 
                placeholder="Enter your last name" 
                className={registerErrors.lastname ? 'form-input-error' : undefined}
                aria-invalid={Boolean(registerErrors.lastname)}
                value={registerForm.lastname}
                onChange={(e) => {
                  setRegisterForm({ ...registerForm, lastname: e.target.value })
                  if (registerErrors.lastname) setRegisterErrors((prev) => ({ ...prev, lastname: '' }))
                }}
              />
            </div>
            <FormFieldError message={registerErrors.lastname} />
          </div>
          
          <div className="form-group">
            <label htmlFor="reg-email">Email</label>
            <div className="input-wrapper">
              <input 
                type="email" 
                id="reg-email" 
                placeholder="Enter your email" 
                className={registerErrors.email ? 'form-input-error' : undefined}
                aria-invalid={Boolean(registerErrors.email)}
                value={registerForm.email}
                onChange={(e) => {
                  setRegisterForm({ ...registerForm, email: e.target.value })
                  if (registerErrors.email) setRegisterErrors((prev) => ({ ...prev, email: '' }))
                }}
              />
            </div>
            <FormFieldError message={registerErrors.email} />
          </div>
          
          <div className="form-group">
            <label htmlFor="reg-password">Password</label>
            <div className="input-wrapper">
              <input 
                type={showRegPassword ? 'text' : 'password'} 
                id="reg-password" 
                placeholder="Create a password" 
                className={registerErrors.password ? 'form-input-error' : undefined}
                aria-invalid={Boolean(registerErrors.password)}
                value={registerForm.password}
                onChange={(e) => {
                  setRegisterForm({ ...registerForm, password: e.target.value })
                  if (registerErrors.password) setRegisterErrors((prev) => ({ ...prev, password: '' }))
                  if (registerErrors.confirmPassword) {
                    setRegisterErrors((prev) => ({ ...prev, confirmPassword: '' }))
                  }
                }}
              />
              <i 
                className={`fas ${showRegPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle`} 
                id="reg-password-toggle" 
                onClick={toggleRegPassword}
              ></i>
            </div>
            <FormFieldError message={registerErrors.password} />
          </div>

          <div className="form-group">
            <label htmlFor="reg-confirm-password">Confirm password</label>
            <div className="input-wrapper">
              <input
                type={showRegConfirmPassword ? 'text' : 'password'}
                id="reg-confirm-password"
                placeholder="Re-enter your password"
                className={registerErrors.confirmPassword ? 'form-input-error' : undefined}
                aria-invalid={Boolean(registerErrors.confirmPassword)}
                value={registerForm.confirmPassword}
                onChange={(e) => {
                  setRegisterForm({ ...registerForm, confirmPassword: e.target.value })
                  if (registerErrors.confirmPassword) {
                    setRegisterErrors((prev) => ({ ...prev, confirmPassword: '' }))
                  }
                }}
              />
              <i
                className={`fas ${showRegConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle`}
                onClick={toggleRegConfirmPassword}
              ></i>
            </div>
            <FormFieldError message={registerErrors.confirmPassword} />
          </div>
          
          <div className="form-options">
            <label className={`checkbox-wrapper${registerErrors.terms ? ' checkbox-wrapper--error' : ''}`}>
              <input 
                type="checkbox" 
                id="agree-terms" 
                aria-invalid={Boolean(registerErrors.terms)}
                checked={registerForm.agreeTerms}
                onChange={(e) => {
                  setRegisterForm({ ...registerForm, agreeTerms: e.target.checked })
                  if (registerErrors.terms) setRegisterErrors((prev) => ({ ...prev, terms: '' }))
                }}
              />
              <span className="checkmark"></span>
              I agree to the <Link to="/terms" className="terms-link">Terms of Service</Link>
            </label>
            <FormFieldError message={registerErrors.terms} />
          </div>

          {captcha.showWidget && (
            <div className="form-group">
              <CaptchaWidget
                siteKey={captcha.siteKey}
                onTokenChange={captcha.onTokenChange}
                onReadyChange={captcha.onReadyChange}
              />
            </div>
          )}
          
          <button type="submit" className="form-btn">
            <i className="fas fa-user-plus"></i>
            Create Account
          </button>
        </form>
        
        {/* Success Message */}
        <div id="form-success" style={{ display: showSuccess ? 'block' : 'none' }}>
          <div className="success-icon">✓</div>
          <h4>Welcome to ZIMARK Education!</h4>
          <p>Your account has been created successfully. You can now access all our features.</p>
          <a href={humanTutorLayerEnabled ? '/my-tutor' : '/messages'} className="dashboard-link">Go to Dashboard</a>
        </div>
        
        <div className="divider">
          <span>or continue with</span>
        </div>
        
            <div className="social-login">
              <GoogleSignInButton
                onSuccess={(role) => {
                  onClose()
                  redirectForRole(role)
                }}
              />
              <button type="button" className="social-btn facebook-btn" disabled title="Coming later">
                <i className="fab fa-facebook-f"></i>
                Facebook
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginModal
